import { registerActions } from "../actionRegistry";
/**
 * Search Functions
 *
 * Handles various search functionality throughout the legacy application.
 * Migrated from js.php.
 */

import { getDomElement, queryAll, setText } from "../utils/dom";
import { load } from "../ui/contentLoader";
import { getCookie } from "./cookies";
import { mountTopSearchDropdown, unmountTopSearchDropdown } from "./components/TopSearchDropdown";
import { mockupFilter } from "./mockupBrowser";

// Search timers and caches
let searchDirectoryTimer: ReturnType<typeof setTimeout> | null = null;
let searchDirectoryCache = "";
let secSearchCache = "";

/**
 * Timed directory search with debounce
 */
export function timedsearchdirectory(): void {
  if (searchDirectoryTimer) {
    clearTimeout(searchDirectoryTimer);
  }
  searchDirectoryTimer = setTimeout(searchdirectory, 500);
}

/**
 * Search within artwork directory (filters visible items)
 */
export function searchdirectory(): void {
  const searchEl = getDomElement<HTMLInputElement>("search-directory");
  if (!searchEl) return;

  const query = searchEl.value;
  if (query === searchDirectoryCache) return;

  searchDirectoryCache = query;
  const artworks = queryAll<HTMLElement>(".directory-artwork");

  artworks.forEach((el) => {
    const id = el.id.replace("artwork-", "");
    const titleEl = getDomElement<HTMLInputElement>(`artwork-title-${id}`);
    if (!titleEl) return;

    const artworkName = titleEl.value;
    const show = query === "" || artworkName.toLowerCase().includes(query.toLowerCase());

    if (show) {
      const cssStyle = getCookie("PictufyModalLayout") === "list" ? "block" : "inline-block";
      el.style.display = cssStyle;
    } else {
      el.style.display = "none";
    }
  });
}

/**
 * Check and hide instant top search
 */
export function checkitopsearch(): void {
  unmountTopSearchDropdown();
}

/**
 * Hide the instant top search dropdown
 */
export function hideitopsearch(): void {
  unmountTopSearchDropdown();
}

/**
 * Show the instant top search dropdown (no-op — dropdown shows on search)
 */
export function showitopsearch(): void {
  // Dropdown is shown automatically when itopsearch() is called with a query
}

/**
 * Instant top search - shows results as you type (delegates to Preact island)
 */
export function itopsearch(q: string): void {
  mountTopSearchDropdown(q);
}

/**
 * Execute instant top search (no-op — handled by Preact island)
 */
export function itopsearchdo(_q: string): void {
  // Handled internally by TopSearchDropdown component
}

/**
 * Secondary search - filters and reloads content
 */
export function secsearch(): void {
  const searchEl = getDomElement<HTMLInputElement>("sec-search");
  if (!searchEl) return;

  const q = searchEl.value;
  if (secSearchCache === q) return;

  const targetEl = getDomElement(getDomElement<HTMLInputElement>("lm_target")?.value ?? "");
  if (targetEl) {
    setText(targetEl, "");
  }

  const fromEl = getDomElement<HTMLInputElement>("lm_from");
  if (fromEl) {
    fromEl.value = "0";
  }

  // Build filter JSON
  const item: Record<string, string> = { q };

  const lightboxEl = getDomElement<HTMLInputElement>("lightbox_id");
  if (lightboxEl) {
    item.lightbox_id = lightboxEl.value;
  }

  const collectionEl = getDomElement<HTMLInputElement>("collection_id");
  if (collectionEl) {
    item.collection_id = collectionEl.value;
  }

  const artistEl = getDomElement<HTMLInputElement>("artist_id");
  if (artistEl) {
    item.artist_id = artistEl.value;
  }

  const optionsEl = getDomElement<HTMLInputElement>("lm_options");
  if (optionsEl) {
    item.options = optionsEl.value;
  }

  const filtersEl = getDomElement<HTMLInputElement>("lm_filters");
  if (filtersEl) {
    filtersEl.value = JSON.stringify([item]);
  }

  const activeEl = getDomElement<HTMLInputElement>("lm_active");
  if (activeEl) {
    activeEl.value = "1";
  }

  load();

  secSearchCache = q;
}

/**
 * Dashboard search (filters partner/artist rows)
 */
export function dashboardsearch(): void {
  const searchEl = getDomElement<HTMLInputElement>("dashboard-search");
  if (!searchEl) return;

  const query = searchEl.value;
  const rows = queryAll<HTMLElement>(".dashboard-compact");

  rows.forEach((el) => {
    const id = el.id.replace("partnercompact-", "");
    const nameEl = getDomElement(`partner-name-${id}`);
    if (!nameEl) return;

    const partnerName = nameEl.innerHTML;
    let show = query === "" || partnerName.toLowerCase().includes(query.toLowerCase());

    // Also check email
    const emailEl = getDomElement<HTMLInputElement>(`artistemail-${id}`);
    if (emailEl?.value === query) {
      show = true;
    }

    el.style.display = show ? "table-row" : "none";
  });
}

/**
 * Modal search (filters lightbox rows)
 */
export function modalsearch(): void {
  const searchEl = getDomElement<HTMLInputElement>("modal-search");
  if (!searchEl) return;

  const query = searchEl.value;
  const rows = queryAll<HTMLElement>(".modal-addlightbox-row");

  rows.forEach((el) => {
    const id = el.id.replace("list-item-", "");
    const nameEl = getDomElement(`modal-togglelist-name-${id}`);
    if (!nameEl) return;

    const listName = nameEl.innerHTML;
    let show = query === "" || listName.toLowerCase().includes(query.toLowerCase());

    // Also check info field
    const infoEl = getDomElement(`modal-togglelist-info-${id}`);
    if (infoEl) {
      const infoName = infoEl.innerHTML;
      if (infoName.toLowerCase().includes(query.toLowerCase())) {
        show = true;
      }

      // Special country: prefix search
      if (query.toLowerCase().includes("country:")) {
        const countryQuery = query.split("country:")[1];
        show = infoName.toLowerCase() === countryQuery?.toLowerCase();
      }
    }

    el.style.display = show ? "block" : "none";
  });
}

let mockupSearchTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Mockup search — debounces to mockupFilter
 */
export function mockupsearch(): void {
  if (mockupSearchTimer) clearTimeout(mockupSearchTimer);
  mockupSearchTimer = setTimeout(mockupFilter, 200);
}

registerActions({
  searchdirectory,
  checkitopsearch,
  itopsearchdo,
  secsearch,
  dashboardsearch,
  mockupsearch,
});
